import { extractGroupIdsFromFilters } from '@disco/utils/search-page'

import type { FilterOperator, FilterState } from 'types/filter-page'

// Field names (from useFiltersDefinition) that map to the legacy search
// params outside the metadata `filters` bucket.
const GROUP_FIELDS = new Set(['look_in_inboxes', 'look_in_channels'])
const TRACK_TAGS_FIELD = 'track_tags'
const DATE_FIELD = 'date'
const DURATION_FIELD = 'duration'

// Subset of the legacy `search` reducer params this bridge writes. Every
// bucket is rewritten on each call so cleared filters reset to defaults
// (mirrors how the search page rebuilds its query from the full FilterState).
export type LegacySearchParams = {
  filters: Array<{
    key: string
    value?: unknown
    start?: unknown
    end?: unknown
    type?: string
  }>
  nativeTags: number[]
  groups: number[]
  startDate: Date
  endDate: Date | null
  duration: string | null
}

const isNumeric = (value: unknown): boolean =>
  typeof value === 'number' ||
  (typeof value === 'string' && /^[0-9]+$/.test(value))

/**
 * V2 filter operators (what the +Filter badges emit) → the legacy metadata
 * filter `{ type, value }` that getFilterValue in SearchActions understands.
 *
 * Legacy only has strict comparisons: 'more than' resolves to
 * `min = value + 1` and 'less than' to `max = value - 1` (see
 * adjustFilterValue). The inclusive ops the Year/Date forms emit (gte/lte)
 * therefore need the value pre-shifted by one so the boundary survives —
 * gte(2020) becomes 'more than' 2019, which resolves back to min = 2020.
 *
 * That shift only applies to numerics: adjustFilterValue leaves non-numeric
 * (date) strings untouched, so for those 'more than'/'less than' are already
 * inclusive and the raw value is correct.
 */
const mapOperatorToLegacy = (
  operator: FilterOperator | undefined,
  value: unknown,
): { type?: string; value: unknown } => {
  switch (operator) {
    case 'gt':
      return { type: 'more than', value }
    case 'lt':
      return { type: 'less than', value }
    case 'gte':
      return {
        type: 'more than',
        value: isNumeric(value) ? Number(value) - 1 : value,
      }
    case 'lte':
      return {
        type: 'less than',
        value: isNumeric(value) ? Number(value) + 1 : value,
      }
    default:
      // equals / contains / not_equals → plain value match ('between' is
      // handled by the caller, which needs start/end rather than value).
      return { value }
  }
}

const isEmpty = (value: unknown): boolean =>
  value === undefined ||
  value === null ||
  (typeof value === 'string' && value.trim().length === 0) ||
  (Array.isArray(value) && value.length === 0)

/**
 * Translates the generic FilterState[] the "+ Filter" badges produce into the
 * legacy `search` reducer params consumed by getSearchResults / the explorer
 * pane. See the search page's transformFiltersToV2 for the source-side model.
 */
export const filterStateToSearchParams = (
  filterStates: FilterState[],
): LegacySearchParams => {
  const params: LegacySearchParams = {
    filters: [],
    nativeTags: [],
    groups: [],
    startDate: new Date(0),
    endDate: null,
    duration: null,
  }

  for (const filter of filterStates) {
    const { definition, value, operator } = filter
    const field = definition.field
    if (!field || isEmpty(value)) continue

    // Groups ("Look in") — reuse the search-page extractor (expects Group[]).
    if (GROUP_FIELDS.has(field)) {
      const ids = extractGroupIdsFromFilters([filter]) ?? []
      params.groups.push(...ids)
      continue
    }

    // Track tags → native tags (value is already number[] of tag ids).
    if (field === TRACK_TAGS_FIELD) {
      if (Array.isArray(value)) {
        params.nativeTags.push(...(value as number[]))
      }
      continue
    }

    // "Date ingested" → startDate / endDate (Date objects for
    // getSearchResults). Matched on field, NOT `type === 'date'`: date-typed
    // *metadata* (e.g. Release Date) is a normal metadata filter and must not
    // be applied as the ingest date.
    if (field === DATE_FIELD) {
      if (Array.isArray(value) && value.length === 2) {
        params.startDate = new Date(value[0])
        params.endDate = new Date(value[1])
      } else if (typeof value === 'string') {
        params.startDate = new Date(value)
        params.endDate = new Date(value)
      }
      continue
    }

    // Duration → "min-max" string (value is [start, end], end may be Infinity).
    if (field === DURATION_FIELD) {
      if (Array.isArray(value) && value.length === 2) {
        const [start, end] = value as [number, number]
        params.duration = `${Math.round(start)}-${
          end === Infinity ? '' : Math.round(end)
        }`
      }
      continue
    }

    // Everything else → a legacy metadata filter object.
    if (operator === 'between' && Array.isArray(value) && value.length === 2) {
      params.filters.push({
        key: field,
        type: 'range',
        start: value[0],
        end: value[1],
      })
      continue
    }

    const mapped = mapOperatorToLegacy(operator, value)
    params.filters.push({
      key: field,
      value: mapped.value,
      ...(mapped.type ? { type: mapped.type } : {}),
    })
  }

  return params
}
